import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { api, useAuth, userModules } from "@/context/AuthContext";
import { pmCategories } from "@/components/pm/pmShared";
import SummaryView from "@/components/SummaryView";
import WorkersView from "@/components/WorkersView";
// Modul berat: dimuat lazy per-tab agar load awal lebih cepat.
const HistoryView = lazy(() => import("@/components/HistoryView"));
const KeuanganView = lazy(() => import("@/components/KeuanganView"));
const TagihanVendorView = lazy(() => import("@/components/keuangan/TagihanVendorView"));
const PettyCashView = lazy(() => import("@/components/petty/PettyCashView"));
const CutiView = lazy(() => import("@/components/cuti/CutiView"));
const AdminView = lazy(() => import("@/components/AdminView"));
const AreaView = lazy(() => import("@/components/AreaView"));
const BarangView = lazy(() => import("@/components/BarangView"));
const HelpdeskView = lazy(() => import("@/components/HelpdeskView"));
const AssetView = lazy(() => import("@/components/AssetView"));
const BAView = lazy(() => import("@/components/BAView"));
const PreventiveMaintenanceView = lazy(() => import("@/components/PreventiveMaintenanceView"));
const LemburView = lazy(() => import("@/components/LemburView"));
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import MasterView from "@/components/master/MasterView";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, LogOut, Menu, X, History, KeyRound, Wallet, UserCog, Map, Package, FileText, Headset, Archive, MonitorPlay, Wrench, Timer, ChevronDown, Loader2, Receipt, CalendarOff, Globe2, ArrowLeft, Sparkles, Coins } from "lucide-react";

const NAV = [
  { key: "beranda", label: "DASHBOARD", icon: LayoutDashboard },
  { key: "area", label: "Area Pengelolaan", icon: Map },
  { key: "pekerja", label: "Data Pekerja", icon: Users },
  { key: "keuangan", label: "Keuangan", icon: Wallet },
  { key: "barang", label: "Consumable", icon: Package },
  { key: "asset", label: "Pencatatan Asset", icon: Archive },
  { key: "pm", label: "Preventive Maintenance", icon: Wrench },
  { key: "ba", label: "BA Pekerjaan", icon: FileText },
  { key: "helpdesk", label: "Helpdesk", icon: Headset },
  { key: "riwayat", label: "Riwayat", icon: History },
  { key: "admin", label: "Kelola Admin", icon: UserCog, adminOnly: true },
];

const CHILDREN = {
  pekerja: [
    { key: "pekerja", label: "Daftar Pekerja", icon: Users },
    { key: "lembur", label: "Lembur Pekerja", icon: Timer },
    { key: "cuti", label: "Pengajuan Cuti", icon: CalendarOff },
  ],
  keuangan: [
    { key: "keuangan", label: "Kontrak", icon: Wallet },
    { key: "tagihan", label: "Tagihan Vendor", icon: Receipt },
    { key: "petty", label: "Petty Cash", icon: Coins },
  ],
  pm: [
    { key: "pm_eng", label: "PM Engineering", icon: Wrench },
    { key: "pm_hk", label: "PM Housekeeping", icon: Sparkles },
  ],
};

// Tab yang memakai hak akses modul lain.
const TAB_MODULE = { tagihan: "keuangan", petty: "petty_cash", cuti: "pekerja", pm_eng: "pm", pm_hk: "pm" };
const PM_TAB_KATEGORI = { pm_eng: "ENGINEERING", pm_hk: "HOUSEKEEPING" };

// Modul masih dalam pengembangan — disembunyikan dari live (kode tetap ada).
// Tab yang terbuka untuk semua akun yang sudah login.
const OPEN_TABS = ["cuti"];

const HIDDEN_MODULES = ["pm", "pm_eng", "pm_hk"];

const TITLES = { beranda: "DASHBOARD FMS MOR JBB", pekerja: "Data Pekerja", area: "Area Pengelolaan", keuangan: "Pencatatan Keuangan — Kontrak", tagihan: "Tagihan Vendor", petty: "Petty Cash", cuti: "Form Pengajuan Cuti", barang: "Rekap Consumable Barang", asset: "Pencatatan Asset", pm: "Preventive Maintenance", pm_eng: "Preventive Maintenance — Engineering", pm_hk: "Preventive Maintenance — Housekeeping", lembur: "Lembur Pekerja", ba: "Berita Acara Pekerjaan", helpdesk: "Helpdesk Service Center", riwayat: "Riwayat Perubahan", admin: "Kelola Admin" };

export default function Dashboard() {
  const { user, logout, activeArea, setActiveArea } = useAuth();
  const [tab, setTab] = useState("beranda");
  const [areaInfo, setAreaInfo] = useState(null);
  useEffect(() => {
    if (!user?.is_master || !activeArea) { setAreaInfo(null); return; }
    api.get("/auth/me").then((r) => setAreaInfo(r.data.area)).catch(() => setAreaInfo(null));
  }, [user, activeArea]);
  const [groupOpen, setGroupOpen] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const mods = useMemo(() => userModules(user), [user]);
  const [pmValidasi, setPmValidasi] = useState(0);
  useEffect(() => {
    if (!mods.includes("pm")) { setPmValidasi(0); return; }
    const cats = pmCategories(user);
    const load = () => api.get("/pm/notifikasi")
      .then((res) => setPmValidasi((res.data.menunggu_validasi || []).filter((t) => cats.includes(t.kategori)).length))
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [mods, user]);
  const bolehTab = useCallback((key) => {
    if (HIDDEN_MODULES.includes(key)) return false;
    if (PM_TAB_KATEGORI[key]) return mods.includes("pm") && pmCategories(user).includes(PM_TAB_KATEGORI[key]);
    return OPEN_TABS.includes(key) ? true : (key === "admin" ? isAdmin : mods.includes(TAB_MODULE[key] || key));
  }, [isAdmin, mods, user]);

  // Prefetch chunk modul di latar belakang agar buka modul terasa instan (hilangkan spinner saat klik pertama).
  useEffect(() => {
    const t = setTimeout(() => {
      import("@/components/BarangView");
      import("@/components/BAView");
      import("@/components/AssetView");
      import("@/components/HelpdeskView");
      import("@/components/AreaView");
      import("@/components/KeuanganView");
      import("@/components/HistoryView");
      import("@/components/LemburView");
      if (isAdmin) import("@/components/AdminView");
    }, 1500);
    return () => clearTimeout(t);
  }, [isAdmin]);
  const navItems = NAV.filter((n) => bolehTab(n.key) || (CHILDREN[n.key] && CHILDREN[n.key].some((c) => bolehTab(c.key))));
  useEffect(() => {
    if (!bolehTab(tab)) {
      const first = NAV.find((n) => bolehTab(n.key));
      if (first) setTab(first.key);
    }
  }, [bolehTab, tab]);

  const SidebarContent = () => (
    <>
      <div className="flex items-center px-6 h-16 border-b border-slate-200">
        <img src="/logo-patra.png" alt="PATRA Facility Management" className="h-11 w-auto" data-testid="sidebar-logo" />
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((n) => {
          const Icon = n.icon;
          const kids = CHILDREN[n.key]?.filter((c) => bolehTab(c.key));
          if (kids && kids.length === 1) {
            const only = kids[0];
            const OIcon = only.icon;
            const oActive = tab === only.key;
            return (
              <button key={n.key} onClick={() => { setTab(only.key); setMobileOpen(false); }}
                data-testid={`nav-${only.key}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  oActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <OIcon className="h-4 w-4" />
                <span className="flex-1 text-left">{only.label}</span>
              </button>
            );
          }
          if (kids && kids.length > 1) {
            const expanded = groupOpen[n.key] ?? kids.some((c) => c.key === tab);
            return (
              <div key={n.key}>
                <button onClick={() => setGroupOpen((g) => ({ ...g, [n.key]: !expanded }))}
                  data-testid={`nav-group-${n.key}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{n.label}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "" : "-rotate-90"}`} />
                </button>
                {expanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-2">
                    {kids.map((c) => {
                      const CIcon = c.icon;
                      const cActive = tab === c.key;
                      return (
                        <button key={c.key} onClick={() => { setTab(c.key); setMobileOpen(false); }}
                          data-testid={`nav-${c.key}`}
                          className={`w-full flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            cActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                          <CIcon className="h-4 w-4" />
                          <span className="flex-1 text-left">{c.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const active = tab === n.key;
          return (
            <button key={n.key} onClick={() => { setTab(n.key); setMobileOpen(false); }}
              data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              <Icon className="h-4 w-4" />
              <span className="flex-1 text-left">{n.label}</span>
              {n.key === "pm" && pmValidasi > 0 && (
                <span data-testid="nav-pm-badge"
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-violet-600 text-white text-[10px] font-bold">
                  {pmValidasi}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="px-2 mb-3">
          <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || "Admin"}</p>
          <p className="text-xs text-slate-500 truncate">{user?.nomor_pegawai || user?.email}</p>
        </div>
        <Button onClick={() => { setPwOpen(true); setMobileOpen(false); }} variant="outline"
          data-testid="change-password-button"
          className="w-full justify-start gap-3 text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-700 mb-2">
          <KeyRound className="h-4 w-4" /> Ubah Kata Sandi
        </Button>
        <Button onClick={logout} variant="outline" data-testid="logout-button"
          className="w-full justify-start gap-3 text-rose-600 border-slate-200 hover:bg-rose-50 hover:text-rose-700">
          <LogOut className="h-4 w-4" /> Keluar
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {user?.is_master && !activeArea && <MasterView />}
      {(!user?.is_master || activeArea) && <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200 fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-white flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {user?.is_master && activeArea && (
          <div className="bg-amber-500 text-slate-950 text-xs font-bold px-4 sm:px-6 h-9 flex items-center gap-2 sticky top-0 z-50" data-testid="master-area-banner">
            <Globe2 className="h-3.5 w-3.5" />
            <span className="truncate">Master Admin — Anda berada di area <span className="underline decoration-2">{areaInfo?.nama || activeArea}</span></span>
            <button type="button" onClick={() => { setActiveArea(""); setTab("beranda"); }} className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-950 text-amber-300 px-3 py-1 hover:bg-slate-800 transition-colors" data-testid="master-back-button">
              <ArrowLeft className="h-3.5 w-3.5" />Kembali ke Master
            </button>
          </div>
        )}
        {/* Topbar */}
        <header className={`h-16 bg-white/70 backdrop-blur-xl border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 sticky z-40 ${user?.is_master && activeArea ? "top-9" : "top-0"}`}>
          <button className="lg:hidden text-slate-700" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">
            {TITLES[tab]}
          </h1>
          {user?.role === "admin" && (
            <button type="button" onClick={() => window.open("/live-monitor", "_blank")}
              data-testid="live-monitor-header-button"
              title="Live Monitor BA & Helpdesk untuk layar TV"
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100 transition-colors">
              <MonitorPlay className="h-3.5 w-3.5" /> Live Monitor
            </button>
          )}
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>}>
          {tab === "beranda" && mods.includes("beranda") && <SummaryView onNavigate={setTab} />}
          {tab === "pekerja" && mods.includes("pekerja") && <WorkersView />}
          {tab === "area" && mods.includes("area") && <AreaView />}
          {tab === "keuangan" && mods.includes("keuangan") && <KeuanganView />}
          {tab === "barang" && mods.includes("barang") && <BarangView />}
          {tab === "asset" && mods.includes("asset") && <AssetView />}
          {PM_TAB_KATEGORI[tab] && mods.includes("pm") && <PreventiveMaintenanceView kategori={PM_TAB_KATEGORI[tab]} />}
          {tab === "lembur" && mods.includes("lembur") && <LemburView />}
          {tab === "tagihan" && mods.includes("keuangan") && <TagihanVendorView />}
          {tab === "petty" && mods.includes("petty_cash") && <PettyCashView />}
          {tab === "cuti" && <CutiView />}
          {tab === "ba" && mods.includes("ba") && <BAView />}
          {tab === "helpdesk" && mods.includes("helpdesk") && <HelpdeskView />}
          {tab === "riwayat" && mods.includes("riwayat") && <HistoryView />}
          {tab === "admin" && isAdmin && <AdminView />}
          </Suspense>
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      </>}
    </div>
  );
}
